import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, ArrowUpRight, ArrowDownRight, Activity, ShieldCheck, Loader2,
  CheckCircle2, Zap, Timer, Radar, ChevronUp, ChevronDown, Check, Search,
  TrendingUp, Cpu, Waves, AlertTriangle, Building2, Globe, History, Clock,
  MinusCircle, Trash2, ExternalLink, X,
} from "lucide-react";

const ANALYSIS_TIMES = [15, 30, 60, 120];
const TIMEFRAMES = [
  { value: "M1", label: "M1 · ✅ Recommended" },
  { value: "M5", label: "M5 · ✅ Recommended" },
  { value: "M15", label: "M15" },
  { value: "S15", label: "S15 · ⚠️ Ap vini (Coming soon)", disabled: true },
  { value: "S30", label: "S30 · ⚠️ Ap vini (Coming soon)", disabled: true },
];
const EXPIRATIONS = ["1 min", "2 min", "5 min", "Auto"];
const TIMEZONES = ["auto", "UTC-5", "UTC-4", "UTC-3", "UTC+0", "UTC+1", "UTC+2", "UTC+3"];
const NO_SIGNAL_LABELS = {
  low_confidence: "Konfyans Ba — Pa Fòse",
  low_volatility: "Mache Twò Kalm",
  news: "Filtè News Aktif",
  otc: "Done Reyèl Pa Disponib (OTC)",
  data_unavailable: "Done Mache Pa Disponib",
  coming_soon: "Ap Vini Byento",
};
const TZ_LABEL = { auto: "Auto Detect" };

function fmtTime(iso, tz) {
  if (!iso) return "--:--:--";
  const d = new Date(iso);
  if (!tz || tz === "auto") return d.toLocaleTimeString([], { hour12: false });
  const m = tz.match(/UTC([+-]\d+)/);
  if (!m) return d.toLocaleTimeString([], { hour12: false });
  const off = parseInt(m[1], 10);
  const utcMs = d.getTime() + d.getTimezoneOffset() * 60000;
  return new Date(utcMs + off * 3600000).toLocaleTimeString([], { hour12: false });
}
// Generic engine phases — internal logic is intentionally hidden from users.
const STEPS = [
  { label: "Connecting to live market feed", icon: Waves },
  { label: "Reading real-time price action", icon: Activity },
  { label: "Running Autotrade decision models", icon: Cpu },
  { label: "Cross-checking signal strength", icon: Radar },
  { label: "Applying risk filter", icon: ShieldCheck },
  { label: "Finalizing recommendation", icon: CheckCircle2 },
];

function Seg({ options, value, onChange, testidPrefix, fmt }) {
  return (
    <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0,1fr))` }}>
      {options.map((o) => {
        const active = value === o;
        return (
          <button key={o} data-testid={`${testidPrefix}-${String(o).toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => onChange(o)}
            className={`h-10 rounded-xl text-sm font-semibold transition-colors border ${active ? "text-white border-transparent" : "text-[#7f9bc4] border-[#14233d] bg-[#060c18] hover:text-white"}`}
            style={active ? { backgroundColor: "var(--sfx-accent)", boxShadow: "0 0 16px var(--sfx-accent-glow)" } : undefined}>
            {fmt ? fmt(o) : o}
          </button>
        );
      })}
    </div>
  );
}

function PairCombobox({ value, onChange, pairs }) {
  const [text, setText] = useState(value || "");
  const [openList, setOpenList] = useState(false);
  useEffect(() => { setText(value || ""); }, [value]);
  const q = (text || "").trim().toLowerCase().replace(/[\s/]/g, "");
  const matches = (q ? pairs.filter((p) => p.toLowerCase().replace(/[\s/]/g, "").includes(q)) : pairs).slice(0, 8);
  const show = openList && matches.length > 0;
  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#5f7ba6] pointer-events-none" />
        <Input
          data-testid="signal-pair-input"
          value={text}
          onChange={(e) => { setText(e.target.value.toUpperCase()); setOpenList(true); }}
          onFocus={() => setOpenList(true)}
          onBlur={() => setTimeout(() => setOpenList(false), 150)}
          placeholder="Type your instrument, e.g. XAU/USD"
          autoComplete="off"
          className="mt-2 pl-9 bg-[#060c18] border-[#14233d] rounded-xl uppercase"
        />
      </div>
      {show && (
        <div data-testid="pair-suggestions"
          className="absolute z-50 left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-xl border border-[#14233d] bg-[#0A1120] shadow-2xl">
          {matches.map((p) => (
            <button key={p} type="button"
              data-testid={`pair-option-${p.toLowerCase().replace("/", "-")}`}
              onMouseDown={(e) => { e.preventDefault(); onChange(p); setText(p); setOpenList(false); }}
              className="w-full text-left px-4 py-2.5 text-sm text-[#cfe0f5] hover:bg-[#0f1a2e] hover:text-white flex items-center gap-2 transition-colors">
              <TrendingUp className="h-3.5 w-3.5 text-[#5f7ba6] shrink-0" /> {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BinarySignal() {
  const navigate = useNavigate();
  const [pairs, setPairs] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [asset, setAsset] = useState("");
  const [broker, setBroker] = useState("Pocket Option");
  const [timezone, setTimezone] = useState("auto");
  const [analysisTime, setAnalysisTime] = useState(30);
  const [timeframe, setTimeframe] = useState("M1");
  const [expiration, setExpiration] = useState("1 min");
  const [minConfidence, setMinConfidence] = useState([70]);
  const [settingsOpen, setSettingsOpen] = useState(true);
  const [howOpen, setHowOpen] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [step, setStep] = useState(0);
  const [signal, setSignal] = useState(null);
  const [warnDismissed, setWarnDismissed] = useState(false);
  const [history, setHistory] = useState([]);
  const timers = useRef([]);

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };

  const loadHistory = () => api.get("/signals/history").then(({ data }) => setHistory(data)).catch(() => {});

  useEffect(() => {
    api.get("/signals/assets").then(({ data }) => { setPairs(data); setAsset(data.find((p) => p === "EUR/USD OTC") || data[0]); }).catch(() => {});
    api.get("/signals/brokers").then(({ data }) => { setBrokers(data); }).catch(() => setBrokers(["Pocket Option", "Quotex", "Deriv", "Binomo", "Olymp Trade", "IQ Option"]));
    loadHistory();
    return () => clearTimers();
  }, []);

  const generate = async () => {
    const chosen = (asset || "").trim().toUpperCase();
    if (!chosen) { toast.error("Please type a symbol / pair first"); return; }
    if (pairs.length && !pairs.includes(chosen)) {
      toast.error("Pair not supported. Pick one from the list.");
      return;
    }
    clearTimers();
    setSignal(null); setStep(0); setAnalyzing(true); setWarnDismissed(false);
    const totalMs = analysisTime * 1000;
    const per = totalMs / STEPS.length;
    STEPS.forEach((_, i) => timers.current.push(setTimeout(() => setStep(i + 1), Math.round(per * (i + 1)))));
    const started = Date.now();
    try {
      const { data } = await api.post("/signals/generate", {
        asset: chosen, timeframe, expiration, min_confidence: minConfidence[0],
        analysis_time: analysisTime, timezone, broker,
      });
      const wait = Math.max(600, totalMs - (Date.now() - started));
      timers.current.push(setTimeout(() => {
        setSignal(data); setAnalyzing(false); loadHistory();
      }, wait));
    } catch (e) {
      clearTimers(); setAnalyzing(false);
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const setResult = async (id, result) => {
    try {
      await api.post(`/signals/history/${id}/result`, { result });
      setHistory((h) => h.map((x) => (x.id === id ? { ...x, result: result === "NONE" ? null : result } : x)));
    } catch { toast.error("Could not save result"); }
  };

  const clearHistory = async () => {
    if (!window.confirm("Clear all signal history?")) return;
    try { await api.delete("/signals/history"); setHistory([]); } catch { /* ignore */ }
  };

  const isBuy = signal?.direction === "BUY";
  const progress = Math.min(100, Math.round((step / STEPS.length) * 100));

  return (
    <div className="min-h-screen w-full flex justify-center bg-black md:bg-transparent">
      <div className="relative w-full max-w-[480px] md:max-w-3xl md:mx-auto min-h-screen bg-[#04060d] md:bg-transparent overflow-x-hidden">
        <div className="px-5 pt-6 pb-10 fade-up space-y-5" data-testid="binary-signal-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/app/settings")} data-testid="binary-back-button"
          className="h-9 px-4 rounded-full border border-[#14233d] flex items-center gap-1.5 text-sm font-semibold text-[#7f9bc4] hover:text-white hover:border-white/40 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
            <Radar className="h-6 w-6" style={{ color: "var(--sfx-accent)" }} /> Binary Signal
          </h1>
          <p className="text-[#5f7ba6] text-sm">Smart Scalper Fx Signal Engine · Live Forex</p>
        </div>
      </div>

      {/* Single popup — appears ONLY when the pair/market is actually volatile. Dismissible. */}
      {signal && !analyzing && signal.volatile_market && !warnDismissed && (
        <div data-testid="market-warning-banner"
          className="rounded-2xl border border-[#f5a623]/50 bg-gradient-to-r from-[#f5a623]/15 to-[#0A1120] p-4 flex items-start gap-3 fade-up">
          <AlertTriangle className="h-5 w-5 text-[#f5a623] shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-[#f5c877] text-sm tracking-wide" data-testid="market-warning-title">WARNING · VOLATILE MARKET</div>
            <p className="mt-1 text-[12px] text-[#e8d3a8] leading-relaxed" data-testid="market-warning-message">
              Mache a volatil kounye a — pwoteje kapital ou, redwi taille pozisyon an epi kenbe SL sere. Oswa change pairs.
            </p>
          </div>
          <button onClick={() => setWarnDismissed(true)} data-testid="market-warning-dismiss"
            className="text-[#f5c877] hover:text-white shrink-0"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Settings */}
      <div className="rounded-3xl border border-[#14233d] bg-[#0A1120] overflow-hidden">
        <button type="button" onClick={() => setSettingsOpen((v) => !v)} data-testid="settings-toggle"
          className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left transition-colors hover:bg-[#0f1a2e]/40">
          <div className="flex items-center gap-2 min-w-0">
            <Zap className="h-4 w-4 shrink-0" style={{ color: "var(--sfx-accent)" }} />
            <div className="min-w-0">
              <div className="font-heading font-semibold">Generate Signals Settings</div>
              {!settingsOpen && (
                <div className="font-mono-term text-[11px] text-[#5f7ba6] truncate mt-0.5" data-testid="settings-summary">
                  {broker} · {asset} · {timeframe} · {expiration} · {minConfidence[0]}%+ · {analysisTime}s
                </div>
              )}
            </div>
          </div>
          {settingsOpen
            ? <ChevronUp className="h-4 w-4 text-[#5f7ba6] shrink-0" />
            : <ChevronDown className="h-4 w-4 text-[#5f7ba6] shrink-0" />}
        </button>
        {settingsOpen && (
        <div className="p-5 space-y-5 border-t border-[#14233d]" data-testid="settings-body">
          <div>
            <label className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6] flex items-center gap-1.5"><Building2 className="h-3 w-3" /> Broker</label>
            <select data-testid="broker-select" value={broker} onChange={(e) => setBroker(e.target.value)}
              className="mt-2 w-full h-11 rounded-xl bg-[#060c18] border border-[#14233d] px-3 text-sm text-white">
              {brokers.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>

          <div>
            <label className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6]">Symbol / Pair</label>
            <PairCombobox value={asset} onChange={setAsset} pairs={pairs} />
            <p className="text-[11px] text-[#5f7ba6] mt-1.5">Type your symbol / pair and pick it from the list.</p>
          </div>

          <div>
            <label className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6]">Analysis Time</label>
            <select data-testid="analysis-time-select" value={analysisTime} onChange={(e) => setAnalysisTime(Number(e.target.value))}
              className="mt-2 w-full h-11 rounded-xl bg-[#060c18] border border-[#14233d] px-3 text-sm text-white">
              {ANALYSIS_TIMES.map((o) => <option key={o} value={o}>{o} seconds</option>)}
            </select>
          </div>

          <div>
            <label className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6]">Timeframe</label>
            <select data-testid="timeframe-select" value={timeframe} onChange={(e) => setTimeframe(e.target.value)}
              className="mt-2 w-full h-11 rounded-xl bg-[#060c18] border border-[#14233d] px-3 text-sm text-white">
              {TIMEFRAMES.map((o) => <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>)}
            </select>
            <p className="mt-1.5 text-[10px] text-[#5f7ba6] leading-relaxed">M1/M5 = pi fyab (vre done). S15/S30 ap vini lè gen yon sous done sub-minit.</p>
          </div>

          <div>
            <label className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6]">Expiration Time</label>
            <select data-testid="expiration-select" value={expiration} onChange={(e) => setExpiration(e.target.value)}
              className="mt-2 w-full h-11 rounded-xl bg-[#060c18] border border-[#14233d] px-3 text-sm text-white">
              {EXPIRATIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6]">Min. Confidence Filter</label>
              <span className="font-heading font-bold text-sm" style={{ color: "var(--sfx-accent)" }}>{minConfidence[0]}%</span>
            </div>
            <Slider value={minConfidence} onValueChange={setMinConfidence} min={50} max={95} step={5} data-testid="confidence-slider" />
          </div>

          <div>
            <label className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6] flex items-center gap-1.5"><Globe className="h-3 w-3" /> Timezone</label>
            <select data-testid="timezone-select" value={timezone} onChange={(e) => setTimezone(e.target.value)}
              className="mt-2 w-full h-10 rounded-xl bg-[#060c18] border border-[#14233d] px-3 text-sm text-white">
              {TIMEZONES.map((tz) => <option key={tz} value={tz}>{TZ_LABEL[tz] || tz}</option>)}
            </select>
            <p className="text-[11px] text-[#5f7ba6] mt-1.5">Synchronizes the signal time with your local time.</p>
          </div>

          <Button onClick={() => setSettingsOpen(false)} data-testid="save-settings-button"
            className="w-full h-11 rounded-xl font-heading font-semibold text-white hover:opacity-90"
            style={{ backgroundColor: "var(--sfx-accent)" }}>
            <Check className="h-4 w-4 mr-1.5" /> Save Settings
          </Button>
        </div>
        )}
      </div>

      {/* Generate button */}
      <button onClick={generate} disabled={analyzing || !asset} data-testid="generate-signal-button"
        className="w-full h-16 rounded-2xl flex items-center justify-center gap-2 text-white font-heading font-extrabold text-lg tracking-wide transition-transform duration-200 hover:scale-[1.02] active:scale-95 disabled:opacity-60"
        style={{ backgroundColor: "var(--sfx-accent)", boxShadow: "0 0 34px var(--sfx-accent-glow)" }}>
        {analyzing ? <><Loader2 className="h-5 w-5 animate-spin" /> ANALYZING…</> : <><Radar className="h-5 w-5" /> GENERATE SIGNAL</>}
      </button>

      {/* Analysis progress */}
      {analyzing && (
        <div className="rounded-3xl border border-[#14233d] bg-[#04070d] p-5 space-y-4" data-testid="analysis-progress">
          <div className="flex items-center justify-between">
            <span className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6]">Autotrade Signal Engine · {asset}</span>
            <span className="font-mono-term text-xs" style={{ color: "var(--sfx-accent)" }}>{progress}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-[#14233d] overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, backgroundColor: "var(--sfx-accent)" }} />
          </div>
          <div className="space-y-2.5">
            {STEPS.map((s, i) => {
              const done = i < step; const current = i === step;
              const Icon = s.icon;
              return (
                <div key={s.label} className={`flex items-center gap-3 transition-opacity ${i <= step ? "opacity-100" : "opacity-40"}`}>
                  <span className="h-7 w-7 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: done ? "rgba(34,224,123,0.12)" : "var(--sfx-accent-soft)" }}>
                    {done ? <CheckCircle2 className="h-4 w-4 text-[#22e07b]" /> : current ? <Loader2 className="h-4 w-4 animate-spin" style={{ color: "var(--sfx-accent)" }} /> : <Icon className="h-4 w-4 text-[#5f7ba6]" />}
                  </span>
                  <span className={`text-sm font-medium ${done ? "text-[#22e07b]" : current ? "text-white" : "text-[#5f7ba6]"}`}>{s.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Result — final signal + broker instructions */}
      {signal && !analyzing && (
        signal.no_signal ? (
          <div className="rounded-3xl border border-[#f5a623]/40 overflow-hidden fade-up" data-testid="signal-result">
            <div className="p-6 text-center" style={{ background: "linear-gradient(160deg,rgba(245,166,35,0.14),#0A1120)" }}>
              <MinusCircle className="h-10 w-10 mx-auto text-[#f5a623]" />
              <div className="font-heading text-2xl font-extrabold mt-3" data-testid="signal-direction">NO SIGNAL</div>
              <div className="font-mono-term text-[11px] uppercase tracking-widest text-[#f5c877] mt-1" data-testid="signal-noreason">{NO_SIGNAL_LABELS[signal.reason_code] || "Pa gen setup fyab"}</div>
              <div className="font-heading text-lg font-bold mt-2" data-testid="signal-asset">{signal.asset}</div>
            </div>
            <div className="p-5 bg-[#0A1120] space-y-3">
              {signal.reason_code !== "otc" && signal.reason_code !== "coming_soon" && signal.reason_code !== "data_unavailable" && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#7f9bc4]">Confidence</span>
                  <span className="font-heading font-bold text-[#f5a623]" data-testid="signal-confidence">{signal.confidence}% (min {signal.min_confidence}%)</span>
                </div>
              )}
              <p className="text-sm text-[#cfe0f5] leading-relaxed" data-testid="signal-explanation">{signal.explanation}</p>
              <div className="flex items-start gap-2.5 rounded-xl border border-[#1b3a5c] bg-[#08111f] p-3.5" data-testid="signal-capital-note">
                <ShieldCheck className="h-4 w-4 text-[#22e07b] mt-0.5 shrink-0" />
                <p className="text-[12px] text-[#9fb6d4] leading-relaxed">
                  <span className="text-white font-semibold">Pwoteje kapital ou.</span> Sistèm nan p ap fòse yon siyal fèb — pa gen setup ki ranpli kondisyon yo kounye a. Tann pwochen opòtinite ki gen konfyans wo. Trading gen <span className="text-[#f5a623] font-semibold">risk elve</span>; janm mize plis pase sa ou ka pèdi.
                </p>
              </div>
            </div>
          </div>
        ) : (
        <div className="rounded-3xl border overflow-hidden fade-up" data-testid="signal-result"
          style={{ borderColor: isBuy ? "rgba(34,224,123,0.4)" : "rgba(255,91,91,0.4)" }}>
          <div className="p-5" style={{ background: isBuy ? "linear-gradient(160deg,rgba(34,224,123,0.14),#0A1120)" : "linear-gradient(160deg,rgba(255,91,91,0.14),#0A1120)" }}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-mono-term text-[10px] uppercase tracking-widest text-[#7f9bc4]">{signal.broker} · {signal.timeframe}</div>
                <div className="font-heading text-2xl font-extrabold" data-testid="signal-asset">{signal.asset}</div>
              </div>
              <div className={`flex items-center gap-1.5 px-4 py-2 rounded-xl font-heading font-extrabold text-lg ${isBuy ? "bg-[#22e07b] text-black" : "bg-[#ff5b5b] text-white"}`} data-testid="signal-direction">
                {isBuy ? <ArrowUpRight className="h-5 w-5" /> : <ArrowDownRight className="h-5 w-5" />}{signal.direction}
              </div>
            </div>

            <div className="mt-4 flex items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono-term text-[10px] uppercase tracking-widest text-[#7f9bc4]">Signal Confidence</span>
                  <span className="font-heading font-bold" data-testid="signal-confidence" style={{ color: isBuy ? "#22e07b" : "#ff5b5b" }}>{signal.confidence}%</span>
                </div>
                <div className="h-2 rounded-full bg-[#14233d] overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${signal.confidence}%`, backgroundColor: isBuy ? "#22e07b" : "#ff5b5b" }} />
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono-term text-[10px] uppercase tracking-widest text-[#7f9bc4] flex items-center gap-1 justify-end"><Timer className="h-3 w-3" /> Expiration</div>
                <div className="font-heading text-lg font-bold" data-testid="signal-expiration">{signal.expiration}</div>
              </div>
            </div>

            {/* Time generated + status */}
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-[#14233d] bg-[#060c18]/60 px-3 py-2">
                <div className="font-mono-term text-[9px] uppercase tracking-widest text-[#7f9bc4] flex items-center gap-1"><Clock className="h-3 w-3" /> Time Generated</div>
                <div className="font-heading font-bold mt-0.5" data-testid="signal-time">{fmtTime(signal.generated_at, timezone)}</div>
              </div>
              <div className="rounded-xl border border-[#22e07b]/30 bg-[#22e07b]/10 px-3 py-2">
                <div className="font-mono-term text-[9px] uppercase tracking-widest text-[#7f9bc4]">Status</div>
                <div className="font-heading font-bold mt-0.5 text-[#22e07b]" data-testid="signal-status">READY</div>
              </div>
            </div>
          </div>

          <div className="p-5 space-y-4 bg-[#0A1120]">
            {/* How to place the trade on the selected broker */}
            <div className="rounded-xl border border-[#14233d] bg-[#060c18] p-4" data-testid="broker-instructions">
              <div className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6] mb-2.5 flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" /> How to place this trade on {signal.broker}</div>
              <ol className="space-y-2">
                {[
                  `Open ${signal.broker}`,
                  `Select the same pair — ${signal.asset}`,
                  `Set the same expiration — ${signal.expiration}`,
                  `Enter your trade manually — press ${signal.direction}`,
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-[#cfe0f5]">
                    <span className="h-5 w-5 rounded-full flex items-center justify-center shrink-0 text-[11px] font-bold" style={{ backgroundColor: "var(--sfx-accent-soft)", color: "var(--sfx-accent)" }}>{i + 1}</span>
                    {t}
                  </li>
                ))}
              </ol>
            </div>

            {signal.strategy && (
              <div className="flex items-center gap-2 flex-wrap rounded-xl border border-[#14233d] bg-[#060c18] px-3 py-2.5" data-testid="signal-strategy">
                {signal.regime && (
                  <span className="font-mono-term text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: "var(--sfx-accent-soft)", color: "var(--sfx-accent)" }}>
                    {signal.regime}
                  </span>
                )}
                <span className="font-heading text-xs font-semibold text-white" data-testid="signal-strategy-name">{signal.strategy}</span>
                {signal.session && <span className="ml-auto font-mono-term text-[9px] text-[#5f7ba6]">{signal.session} session</span>}
              </div>
            )}

            <div className="flex items-center justify-between">
              <div>
                <div className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6]">Live Price</div>
                <div className="font-heading text-lg font-bold mt-0.5" data-testid="signal-price">{signal.price}</div>
              </div>
              {signal.live_data && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#22e07b]/30 bg-[#22e07b]/10" data-testid="signal-live-badge">
                  <span className="h-2 w-2 rounded-full bg-[#22e07b] live-dot" />
                  <span className="font-mono-term text-[10px] uppercase tracking-widest text-[#22e07b]">Live Data</span>
                </div>
              )}
            </div>

            <div>
              <div className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6] mb-1">Autotrade Recommendation</div>
              <p className="text-sm text-[#cfe0f5] leading-relaxed" data-testid="signal-explanation">{signal.explanation}</p>
            </div>

            <p className="text-[10px] text-[#5f7ba6] leading-relaxed pt-1" data-testid="risk-warning">
              Signals are for informational purposes only. Trading involves risk. Users execute trades manually and are responsible for their decisions.
            </p>
          </div>
        </div>
        )
      )}

      {/* Signal History */}
      {history.length > 0 && (
        <div className="rounded-3xl border border-[#14233d] bg-[#0A1120] overflow-hidden" data-testid="signal-history">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#14233d]">
            <div className="flex items-center gap-2">
              <History className="h-4 w-4" style={{ color: "var(--sfx-accent)" }} />
              <span className="font-heading font-semibold">Signal History</span>
            </div>
            <button onClick={clearHistory} data-testid="clear-history" className="text-[#5f7ba6] hover:text-[#ff5b5b] transition-colors"><Trash2 className="h-4 w-4" /></button>
          </div>
          <div className="divide-y divide-[#14233d] max-h-96 overflow-y-auto">
            {history.map((h) => {
              const buy = h.direction === "BUY";
              const none = h.direction === "NO SIGNAL";
              return (
                <div key={h.id} className="px-5 py-3" data-testid={`history-row-${h.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-heading font-bold text-sm truncate">{h.asset}</div>
                      <div className="font-mono-term text-[10px] text-[#5f7ba6]">{h.broker} · {fmtTime(h.generated_at, h.timezone)} · {h.expiration}</div>
                    </div>
                    <div className={`px-2.5 py-1 rounded-lg font-heading font-bold text-xs shrink-0 ${none ? "bg-[#f5a623]/15 text-[#f5a623]" : buy ? "bg-[#22e07b]/15 text-[#22e07b]" : "bg-[#ff5b5b]/15 text-[#ff5b5b]"}`}>
                      {h.direction} · {h.confidence}%
                    </div>
                  </div>
                  {!none && (
                    <div className="flex items-center gap-2 mt-2">
                      <span className="font-mono-term text-[9px] uppercase tracking-widest text-[#5f7ba6]">Result</span>
                      {["WIN", "LOSS"].map((r) => (
                        <button key={r} data-testid={`result-${r.toLowerCase()}-${h.id}`} onClick={() => setResult(h.id, h.result === r ? "NONE" : r)}
                          className={`px-2.5 py-1 rounded-md text-[11px] font-semibold border transition-colors ${h.result === r ? (r === "WIN" ? "bg-[#22e07b] text-black border-transparent" : "bg-[#ff5b5b] text-white border-transparent") : "border-[#14233d] text-[#7f9bc4] hover:text-white"}`}>
                          {r}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* How this bot works (collapsible, generic — no internal logic exposed) */}
      <div className="rounded-3xl border border-[#14233d] bg-[#0A1120] overflow-hidden" data-testid="binary-how-it-works">
        <button type="button" onClick={() => setHowOpen((v) => !v)} data-testid="how-it-works-toggle"
          className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left transition-colors hover:bg-[#0f1a2e]/40">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 shrink-0" style={{ color: "var(--sfx-accent)" }} />
            <span className="font-heading font-semibold">Does this engine actually work?</span>
          </div>
          {howOpen
            ? <ChevronUp className="h-4 w-4 text-[#5f7ba6] shrink-0" />
            : <ChevronDown className="h-4 w-4 text-[#5f7ba6] shrink-0" />}
        </button>
        {howOpen && (
          <div className="p-5 space-y-4 border-t border-[#14233d]" data-testid="how-it-works-body">
            <p className="text-sm text-[#cfe0f5] leading-relaxed">
              Yes. The engine does not guess the market. It connects to{" "}
              <span className="text-white font-semibold">live forex price data</span> and runs it through
              our Autotrade decision models to react to real-time conditions.
            </p>
            <div className="space-y-2.5">
              {[
                { i: Waves, t: "Live Market Feed", d: "Reads real-time prices for every instrument you select." },
                { i: Cpu, t: "Autotrade Decision Models", d: "Weigh momentum, trend and strength behind the scenes." },
                { i: ShieldCheck, t: "Confidence Filter", d: "Only surfaces setups that pass your minimum confidence." },
              ].map(({ i: Icon, t, d }) => (
                <div key={t} className="flex items-start gap-3">
                  <span className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                    style={{ backgroundColor: "var(--sfx-accent-soft)" }}>
                    <Icon className="h-4 w-4" style={{ color: "var(--sfx-accent)" }} />
                  </span>
                  <div>
                    <div className="font-heading font-semibold text-sm text-white">{t}</div>
                    <div className="text-[13px] text-[#7f9bc4] leading-relaxed">{d}</div>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-sm text-[#cfe0f5] leading-relaxed">
              When conditions line up, you get a clear{" "}
              <span className="text-[#22e07b] font-semibold">BUY</span> or{" "}
              <span className="text-[#ff5b5b] font-semibold">SELL</span> recommendation with a confidence score —
              the exact strategy stays private.
            </p>
          </div>
        )}
      </div>
        </div>
      </div>
    </div>
  );
}
